/**
 * brand.ts — the company name and logo shown on screens, read from the TENANT.
 *
 * 🔒 NO SCREEN TYPES THE CLIENT'S NAME (2026-10-10, tour-demo bug). The name
 * comes from `/tenants/current` (`brand.name`, else the tenant name without
 * its legal suffix). Before sign-in — login, forgot/reset password, the
 * driver's public locate page — it comes from the public `/public/brand`.
 *
 * 🔒 THE LIVE SITE MUST NOT CHANGE. This is Veda's own codebase, so the
 * fallback (no tenant yet, platform admin, older backend) is "Veda Agro" — the
 * words the headings always had.
 *
 * 🔒 A MISSING LOGO FILE MUST NOT BREAK THE PAGE. `/veda-agro-logo.png` lives on
 * the box but is not in git; `BrandLogo` shows the name if the image fails.
 */
import { createElement, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/utils/api';

export interface Brand {
  name: string;
  logoUrl: string | null;
}

export const FALLBACK_BRAND = 'Veda Agro';
/** The full legal name the MD views always fell back to. */
export const FALLBACK_LEGAL_NAME = 'Veda Agro Pvt Ltd';
export const DEFAULT_LOGO = '/veda-agro-logo.png';

const LEGAL_SUFFIX = /[\s,]*(pvt\.?\s*ltd\.?|private\s+limited|ltd\.?|limited|llp)\s*$/i;

/** "Veda Agro Pvt Ltd" → "Veda Agro". Empty → null. */
export function stripLegalSuffix(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const out = name.trim().replace(LEGAL_SUFFIX, '').trim();
  return out === '' ? null : out;
}

type TenantLike = { slug?: string; name?: string; isAdmin?: boolean; brand?: Partial<Brand> | null } | null | undefined;

let publicBrand: Brand | null = null;

/** The store's tenant; tolerant of test doubles that mock the store as a bare selector. */
function currentTenant(): TenantLike {
  const get = (useAuthStore as unknown as { getState?: () => { tenant?: unknown } }).getState;
  return (typeof get === 'function' ? get()?.tenant : null) as TenantLike;
}

function fromTenant(t: TenantLike): Brand | null {
  if (!t || t.isAdmin || t.slug === 'platform') return null;
  const name = stripLegalSuffix(t.brand?.name) ?? stripLegalSuffix(t.name);
  if (!name) return null;
  return { name, logoUrl: t.brand?.logoUrl ?? null };
}

/** The brand right now, for code outside React (print, WhatsApp text). */
export function brandNow(): Brand {
  return fromTenant(currentTenant()) ?? publicBrand ?? { name: FALLBACK_BRAND, logoUrl: null };
}

export const brandNameNow = (): string => brandNow().name;

/** First word — "Veda Agro" → "Veda". For "(Veda)" role tags and "Veda Sales app". */
export const brandTag = (): string => brandNameNow().split(/\s+/)[0] || 'Veda';

/** The tenant's full (legal) name, for documents that always carried it. */
export function legalNameNow(): string {
  const t = currentTenant();
  if (t && !t.isAdmin && t.slug !== 'platform' && typeof t.name === 'string' && t.name.trim()) return t.name.trim();
  return FALLBACK_LEGAL_NAME;
}

let publicLoad: Promise<void> | null = null;
/** Pre-auth pages: fetch the box's public brand once. Never throws. */
export function loadPublicBrand(): Promise<void> {
  if (!publicLoad) {
    publicLoad = api
      .get('/public/brand')
      .then((r) => {
        const b = r?.data?.data as Partial<Brand> | null | undefined;
        const name = stripLegalSuffix(b?.name);
        publicBrand = name ? { name, logoUrl: typeof b?.logoUrl === 'string' ? b.logoUrl : null } : null;
      })
      .catch(() => undefined);
  }
  return publicLoad;
}

/** React hook: re-renders when the tenant (or the public brand) arrives. */
export function useBrand(): Brand {
  const tenant = useAuthStore((s) => s.tenant);
  const [, bump] = useState(0);
  useEffect(() => {
    if (fromTenant(tenant as TenantLike) || publicBrand) return;
    let live = true;
    void loadPublicBrand().then(() => live && bump((n) => n + 1));
    return () => {
      live = false;
    };
  }, [tenant]);
  return fromTenant(tenant as TenantLike) ?? publicBrand ?? { name: FALLBACK_BRAND, logoUrl: null };
}

/** Logo with a text fallback: a missing file shows the name, never a broken image. */
export function BrandLogo({ className }: { className?: string }) {
  const brand = useBrand();
  const src = brand.logoUrl || DEFAULT_LOGO;
  const [failed, setFailed] = useState<string | null>(null);
  if (failed === src) {
    return createElement(
      'div',
      { className: 'text-2xl font-semibold', style: { color: 'var(--ink-0)' } },
      brand.name,
    );
  }
  return createElement('img', { src, alt: brand.name, className, onError: () => setFailed(src) });
}
